import { Component, Suspense, lazy, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './components/chrome.js';
import { ScrollToTop } from './components/scroll-to-top.js';
import { useMe } from './lib/api.js';
import { Skeleton } from './components/ui.js';

/**
 * Routing policy, stated once here: the marketing surfaces are public, everything under `/app` requires a
 * session, and the workspace never renders a "please log in" screen inside the shell — an unauthenticated
 * visitor is redirected, because the app's URL is also the analyst's bookmark.
 */
const Landing = lazy(() => import('./pages/Landing.js'));
const Docs = lazy(() => import('./pages/Docs.js'));
const Login = lazy(() => import('./pages/Login.js'));
const FleetWall = lazy(() => import('./pages/FleetWall.js'));
const AssetWorkspace = lazy(() => import('./pages/AssetWorkspace.js'));
const AnomalyQueue = lazy(() => import('./pages/AnomalyQueue.js'));
const DossierReview = lazy(() => import('./pages/DossierReview.js'));
const Drafts = lazy(() => import('./pages/Drafts.js'));
const Ingest = lazy(() => import('./pages/Ingest.js'));
const Knowledge = lazy(() => import('./pages/Knowledge.js'));
const Account = lazy(() => import('./pages/Account.js'));
const Admin = lazy(() => import('./pages/Admin.js'));
const Pilot = lazy(() => import('./pages/Pilot.js'));
const System = lazy(() => import('./pages/System.js'));

export function App(): React.ReactNode {
  const location = useLocation();
  // The session check runs on every route, not only under /app: the public landing page uses it to switch
  // its primary button between "sign in" and "open workspace", and a 401 there is the honest answer.
  const { data, loading } = useMe();
  const authenticated = Boolean(data);

  return (
    <Suspense fallback={<PageSkeleton />}>
      <ScrollToTop />
      {/* Keyed by pathname so navigating away is a real reset: a page that threw cannot hold the app hostage. */}
      <RouteErrorBoundary key={location.pathname}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/how-it-works" element={<Docs />} />
        <Route path="/login" element={authenticated && data ? <Navigate to="/app" replace /> : <Login />} />
        <Route
          path="/app"
          element={
            loading && !authenticated ? (
              <PageSkeleton />
            ) : !authenticated || !data ? (
              <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />
            ) : (
              <AppShell me={data} />
            )
          }
        >
          <Route index element={<FleetWall />} />
          <Route path="anomalies" element={<AnomalyQueue />} />
          <Route path="assets/:assetId" element={<AssetWorkspace />} />
          <Route path="dossiers/:dossierId" element={<DossierReview />} />
          <Route path="drafts" element={<Drafts />} />
          <Route path="ingest" element={<Ingest />} />
          <Route path="knowledge" element={<Knowledge />} />
          <Route path="account" element={<Account />} />
          <Route path="pilot" element={<Pilot />} />
          <Route path="system" element={<System />} />
          <Route path="admin" element={data?.roleOnActiveSite === 'admin' ? <Admin /> : <Navigate to="/app" replace />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
      </RouteErrorBoundary>
    </Suspense>
  );
}

const PageSkeleton = (): React.ReactNode => (
  <div className="min-h-screen bg-bg p-6">
    <div className="mx-auto max-w-[1440px] grid gap-4">
      <Skeleton rows={2} />
      <div className="grid gap-3">
        <Skeleton rows={6} />
      </div>
    </div>
  </div>
);

const NotFound = (): React.ReactNode => (
  <main className="min-h-[60vh] grid place-items-center p-6">
    <div className="panel max-w-[52ch]">
      <p className="micro-label">404</p>
      <h1 className="text-h3 mt-2">That route does not exist in this product</h1>
      <p className="text-ui text-text-secondary mt-2">
        The workspace lives under <code className="font-mono">/app</code>; the documentation is at <code className="font-mono">/how-it-works</code>.
      </p>
      <a className="btn btn-primary btn-sm mt-4" href="/app">
        Go to the fleet wall
      </a>
    </div>
  </main>
);

/**
 * Last line of defence for the workspace.
 *
 * Without this, a render error anywhere in a page unmounted the entire application and left the operator a
 * blank white document — which is what one malformed panel on `/app/account` did for as long as it existed, and
 * which a screenshot of the page would have looked exactly like nothing. A crashed panel must say so, keep the
 * navigation, and offer the one honest action available: reload, or go back to the fleet wall.
 */
class RouteErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }): void {
    // Kept in the console rather than reported anywhere: this build sends no telemetry, and the message that
    // matters to an engineer is the one the browser already has.
    console.error('page failed to render', error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <main className="min-h-[60vh] grid place-items-center p-6">
        <div className="panel max-w-[58ch]">
          <p className="micro-label">Render fault</p>
          <h1 className="text-h3 mt-2">This page failed to draw</h1>
          <p className="text-ui text-text-secondary mt-2">
            Nothing was sent anywhere and nothing was changed: the failure happened while building the screen from data the platform
            already returned. The rest of the workspace is still usable.
          </p>
          <pre className="mt-3 overflow-x-auto rounded-md border border-border bg-[var(--surface-inset)] p-3 font-mono text-[12px] text-text">
            {error.message || error.name}
          </pre>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => this.setState({ error: null })}>
              Try again
            </button>
            <a className="btn btn-secondary btn-sm" href="/app">
              Fleet wall
            </a>
          </div>
        </div>
      </main>
    );
  }
}
